import { openOAuthConnectPopup } from '../oauth-connect-lifecycle'
import { FC, useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { queryClient, trpc, trpcClient } from '@/lib/trpc';
import { WEB_URL } from '@/lib/env';
import { IntegrationFeature, IntegrationProvider, ConfigModalProps } from '../types';
import { IntegrationIcon } from '../icons';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@deepguide-ai/ui/dialog';
import { Button } from '@deepguide-ai/ui/button';
import { CheckCircle2, Loader2 } from 'lucide-react';

const SlackIconWrapper: FC = () => <IntegrationIcon type="Slack" />;

/**
 * Wires the Integrations page tile + the chat-side connect button into the
 * existing Screenata Slack bot OAuth flow (`screenataBot.initInstall`),
 * which is the same flow used by Settings → Slack Bot. Without this
 * provider both surfaces silently no-op for Slack.
 */
const SlackConfigModal: FC<ConfigModalProps> = ({
  open,
  onOpenChange,
  workspaceId,
  onComplete,
  integration,
}) => {
  const [isConnecting, setIsConnecting] = useState(false);
  const [awaitingAuthResult, setAwaitingAuthResult] = useState(false);
  const isConnected = !!integration?.id;
  const disconnectMutation = useMutation(
    trpc.screenataBot.disconnect.mutationOptions(),
  );

  useEffect(() => {
    if (!open) {
      setIsConnecting(false);
      setAwaitingAuthResult(false);
    }
  }, [open]);

  useEffect(() => {
    if (open && awaitingAuthResult && integration?.id) {
      setIsConnecting(false);
      setAwaitingAuthResult(false);
      onComplete();
    }
  }, [awaitingAuthResult, integration?.id, onComplete, open]);

  const handleConnect = async () => {
    setIsConnecting(true);
    setAwaitingAuthResult(true);
    try {
      await SlackProvider.handleConnect({
        workspaceId,
        onSuccess: () => {
          setIsConnecting(false);
          setAwaitingAuthResult(false);
          onComplete();
        },
        onFinish: (status) => {
          if (status === 'error' || status === 'closed') {
            setIsConnecting(false);
            setAwaitingAuthResult(false);
          }
        },
      });
    } catch (error) {
      console.error('Failed to connect to Slack:', error);
      setIsConnecting(false);
      setAwaitingAuthResult(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isConnected ? 'Slack connected' : 'Connect to Slack'}</DialogTitle>
          <DialogDescription>
            {isConnected
              ? 'Screenata is connected to Slack for daily compliance briefings, evidence collection via DM, and conversational compliance workflows.'
              : 'Add the Screenata bot to your Slack workspace for daily compliance briefings, evidence collection via DM, and conversational compliance workflows.'}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 p-4">
          {isConnected ? (
            <div className="flex items-center gap-2 rounded-sm border border-border-divider bg-background px-3 py-2 text-sm text-text-body">
              <CheckCircle2 className="h-4 w-4 text-[#12B76A]" />
              Slack is connected.
            </div>
          ) : (
            <div className="text-sm text-muted-foreground">
              You'll be redirected to Slack to authorize the bot. Make sure
              pop-ups are enabled for this site.
            </div>
          )}
          <div className="flex justify-end gap-2">
            {!isConnected ? (
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isConnecting}
              >
                Cancel
              </Button>
            ) : null}
            {isConnected ? (
              <Button
                variant="destructive"
                disabled={disconnectMutation.isPending}
                onClick={() => {
                  if (!integration?.id) return;
                  disconnectMutation.mutate(
                    { integrationId: integration.id },
                    {
                      onSuccess: async () => {
                        await queryClient.invalidateQueries({
                          queryKey: trpc.screenataBot.getStatus.queryKey(),
                        });
                        await queryClient.invalidateQueries({
                          queryKey:
                            trpc.integrations.getWorkspaceIntegrations.queryKey(),
                        });
                        onOpenChange(false);
                        onComplete();
                      },
                    },
                  );
                }}
              >
                {disconnectMutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Disconnecting...
                  </>
                ) : (
                  'Disconnect Slack'
                )}
              </Button>
            ) : null}
            {!isConnected ? (
              <Button onClick={handleConnect} disabled={isConnecting}>
                {isConnecting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Connecting...
                  </>
                ) : (
                  'Connect to Slack'
                )}
              </Button>
            ) : null}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const SlackProvider: IntegrationProvider = {
  id: 'Slack',
  name: 'Slack',
  description:
    'Connect Slack for daily briefings, evidence collection, and conversational compliance.',
  Icon: SlackIconWrapper,
  ConfigModal: SlackConfigModal,

  getFeatures: (): IntegrationFeature[] => [
    {
      id: 'briefings',
      name: 'Daily briefings',
      description: 'Post compliance summaries to a channel',
      enabled: true,
    },
    {
      id: 'commands',
      name: 'Slash commands',
      description: 'Run /grc status and other commands inline',
      enabled: true,
    },
  ],

  handleConnect: async ({ redirectUrl, onSuccess, onFinish }) => {
    // Canonical Screenata OAuth callback. The web app mounts a single
    // generic route (`/auth-callback/$provider`) that runs each
    // provider's `onConnectComplete` and publishes a state-bound completion
    // notification. Earlier copies of this code referenced
    // `/api/auth/slack/bot/callback` — that path was never registered
    // anywhere and the popup ended up on a 404.
    const targetRedirectUrl =
      redirectUrl ?? `${WEB_URL || window.location.origin}/auth-callback/Slack`;

    const data = await trpcClient.screenataBot.initInstall.mutate({
      redirectUrl: targetRedirectUrl,
    });

    if (!data?.authUrl) {
      console.error('No Slack install URL returned from initInstall');
      onFinish?.('error');
      return;
    }

    openOAuthConnectPopup({
      authUrl: data.authUrl, windowName: 'slack-auth', provider: 'Slack',
      onSuccess: result => {
        void queryClient.invalidateQueries({ queryKey: trpc.integrations.getWorkspaceIntegrations.queryKey() });
        void queryClient.invalidateQueries({ queryKey: trpc.screenataBot.getStatus.queryKey() });
        onSuccess?.(result);
      },
      onError: message => toast.error(message),
      onPopupBlocked: () => toast.error('OAuth popup was blocked. Allow popups and try again.'),
      onFinish: onFinish,
    })
  },

  onConnectComplete: async ({ code, state }: { code: string; state: string }) => {
    return trpcClient.screenataBot.completeInstall.mutate({ code, state });
  },

  syncData: async () => {
    // No periodic sync — the Slack bot pushes events server-side.
  },

  shouldDisplay: () => true,
};
